'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import {
  Wrench,
  Stethoscope,
  Car,
  Sparkles,
  MessageSquare,
  Search,
  Lightbulb,
  Info,
  Globe,
  Moon,
  Sun,
  LayoutGrid,
  Film,
  ExternalLink,
  Gavel,
  ArrowUpRight,
  BadgeCheck,
  Boxes,
  CircleDollarSign,
  Gauge,
  ShieldCheck,
  Store,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useLanguage } from '@/context/LanguageContext';
import { useCountry, COUNTRIES } from '@/context/CountryContext';
import { useTheme } from '@/context/ThemeContext';
import { TIPS_DATABASE, getContextualTips } from '@/app/lib/tips-data';
import { AIPersonalizedRecommendations } from '@/components/AIPersonalizedRecommendations';
import { useBranding } from '@/context/BrandingContext';
import type { AdItem } from '@/lib/api/ads';
import { adsApi } from '@/lib/api/ads';

function toAbsoluteMediaUrl(raw: string | null | undefined): string {
  if (!raw) return '';
  if (raw.startsWith('http://') || raw.startsWith('https://')) return raw;
  const apiBase = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000';
  return `${apiBase}${raw.startsWith('/') ? '' : '/'}${raw}`;
}

function toYoutubeEmbedUrl(raw: string): string {
  if (!raw) return '';
  try {
    const parsed = new URL(raw);
    const host = parsed.hostname.toLowerCase();
    if (host.includes('youtu.be')) {
      const id = parsed.pathname.replace('/', '').split('/')[0];
      return id ? `https://www.youtube.com/embed/${id}` : '';
    }
    if (host.includes('youtube.com')) {
      if (parsed.pathname === '/watch') {
        const id = parsed.searchParams.get('v') || '';
        return id ? `https://www.youtube.com/embed/${id}` : '';
      }
      if (parsed.pathname.startsWith('/embed/')) {
        return `https://www.youtube.com${parsed.pathname}`;
      }
      if (parsed.pathname.startsWith('/shorts/')) {
        const id = parsed.pathname.split('/')[2] || '';
        return id ? `https://www.youtube.com/embed/${id}` : '';
      }
    }
  } catch {
    return '';
  }
  return '';
}

function toYoutubeVideoId(raw: string): string {
  const embed = toYoutubeEmbedUrl(raw);
  if (!embed) return '';
  const parts = embed.split('/embed/');
  if (parts.length < 2) return '';
  return (parts[1] || '').split('?')[0];
}

export default function Home() {
  const router = useRouter();
  const { t, isRTL, lang, toggleLanguage } = useLanguage();
  const { setSelectedCountryCode } = useCountry();
  const { isDark, toggleTheme } = useTheme();
  const { branding } = useBranding();

  const [currentTipIndex, setCurrentTipIndex] = useState(0);
  const [rentalsEnabled, setRentalsEnabled] = useState(true);
  const [homeAds, setHomeAds] = useState<AdItem[]>([]);
  const [adsLoading, setAdsLoading] = useState(true);
  const [selectedStoryAd, setSelectedStoryAd] = useState<AdItem | null>(null);
  const [generalSearch, setGeneralSearch] = useState('');
  const [generalSearchScope, setGeneralSearchScope] = useState<'all' | 'cars' | 'parts' | 'services' | 'workshops' | 'marketplace'>('all');
  const activeTips = TIPS_DATABASE;

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTipIndex((prev) => (prev + 1) % activeTips.length);
    }, 10000);
    return () => clearInterval(interval);
  }, [activeTips.length]);

  useEffect(() => {
    let mounted = true;
    const API = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000';
    fetch(`${API}/api/settings/public/`)
      .then(async (res) => {
        if (!res.ok) return null;
        return res.json();
      })
      .then((data) => {
        if (!mounted || !data) return;
        const raw = data.rentals_enabled;
        const enabled = !['0', 'false', 'no', 'off'].includes(String(raw || '').toLowerCase());
        setRentalsEnabled(enabled);
      })
      .catch(() => {
        if (mounted) setRentalsEnabled(true);
      });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    setAdsLoading(true);
    adsApi
      .list({ placement: 'homepage' })
      .then((rows) => {
        if (!mounted) return;
        const sorted = [...rows].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
        setHomeAds(sorted.slice(0, 3));
      })
      .catch(() => {
        if (mounted) setHomeAds([]);
      })
      .finally(() => {
        if (mounted) setAdsLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const heroImage = 'https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?auto=format&fit=crop&w=1800&q=85';

  const mainServices = [
    {
      id: 'buy',
      label: t('buy_car'),
      sub: 'Buy Car',
      desc: lang === 'ar' ? 'سيارات جاهزة للفحص والمقارنة' : 'Verified cars ready to compare',
      icon: Car,
      color: 'text-blue-700 bg-blue-50 ring-blue-100',
      accent: 'from-blue-600 to-sky-400',
      href: '/buy',
      floatClass: 'right-2 top-4',
    },
    {
      id: 'sell',
      label: t('sell_car'),
      sub: 'Sell Car',
      desc: lang === 'ar' ? 'تسعير أسرع ونشر إعلان واضح' : 'Faster pricing and cleaner listings',
      icon: CircleDollarSign,
      color: 'text-orange-700 bg-orange-50 ring-orange-100',
      accent: 'from-orange-500 to-amber-300',
      href: '/sell',
      floatClass: 'left-8 top-10',
    },
    {
      id: 'marketplace',
      label: t('public_market'),
      sub: 'Public Market',
      desc: lang === 'ar' ? 'إعلانات متنوعة للسيارات والملحقات' : 'Open listings for auto goods',
      icon: Store,
      color: 'text-emerald-700 bg-emerald-50 ring-emerald-100',
      accent: 'from-emerald-500 to-teal-300',
      href: '/marketplace',
      floatClass: 'right-12 top-36',
    },
    {
      id: 'parts',
      label: t('parts'),
      sub: 'Spare Parts',
      desc: lang === 'ar' ? 'قطع غيار حسب السيارة والمتجر' : 'Parts by vehicle and supplier',
      icon: Boxes,
      color: 'text-blue-700 bg-blue-50 ring-blue-100',
      accent: 'from-blue-600 to-indigo-400',
      href: '/parts',
      floatClass: 'left-2 top-48',
    },
    {
      id: 'auctions',
      label: 'مزادات السيارات',
      sub: 'Car Auctions',
      desc: lang === 'ar' ? 'مزادات واضحة بتفاصيل مباشرة' : 'Live auctions with clear details',
      icon: Gavel,
      color: 'text-amber-700 bg-amber-50 ring-amber-100',
      accent: 'from-amber-500 to-orange-300',
      href: '/auctions',
      floatClass: 'right-24 bottom-8',
    },
    {
      id: 'maintenance',
      label: t('maintenance'),
      sub: 'Workshops',
      desc: lang === 'ar' ? 'ورش وصيانة وخدمات قريبة' : 'Workshops and service nearby',
      icon: Wrench,
      color: 'text-orange-700 bg-orange-50 ring-orange-100',
      accent: 'from-orange-500 to-red-400',
      href: '/maintenance',
      floatClass: 'left-20 bottom-6',
    },
    ...(rentalsEnabled ? [{
      id: 'rentals',
      label: 'تأجير السيارات',
      sub: 'Rentals',
      desc: lang === 'ar' ? 'خيارات يومية وشهرية للقيادة' : 'Daily and monthly driving options',
      icon: Gauge,
      color: 'text-violet-700 bg-violet-50 ring-violet-100',
      accent: 'from-violet-500 to-fuchsia-400',
      href: '/rentals',
      floatClass: 'left-32 top-28',
    }] : []),
    {
      id: 'services',
      label: t('integrated_services'),
      sub: 'Hub',
      desc: lang === 'ar' ? 'كل خدمات المركبات في مسار واحد' : 'One hub for vehicle services',
      icon: LayoutGrid,
      color: 'text-indigo-700 bg-indigo-50 ring-indigo-100',
      accent: 'from-indigo-600 to-blue-400',
      href: '/services',
      floatClass: 'right-4 bottom-28',
    },
  ];

  const aiServices = [
    { id: 'diagnose', label: t('diagnose'), sub: 'AI Diagnose', icon: Stethoscope, color: 'text-emerald-600 bg-emerald-50', href: '/diagnose' },
    { id: 'inspection', label: t('inspection'), sub: 'AI Vision', icon: Sparkles, color: 'text-indigo-600 bg-indigo-50', href: '/inspection' },
    { id: 'advisor', label: 'المستشار الذكي', sub: 'Car Fit Advisor', icon: Car, color: 'text-yellow-600 bg-yellow-50', href: '/buy/advisor' },
  ];

  const searchScopes = [
    { id: 'all', label: lang === 'ar' ? 'الكل' : 'All' },
    { id: 'cars', label: lang === 'ar' ? 'سيارات' : 'Cars' },
    { id: 'parts', label: lang === 'ar' ? 'قطع غيار' : 'Parts' },
    { id: 'services', label: lang === 'ar' ? 'خدمات' : 'Services' },
    { id: 'workshops', label: lang === 'ar' ? 'ورش' : 'Workshops' },
    { id: 'marketplace', label: lang === 'ar' ? 'السوق' : 'Market' },
  ] as const;

  const submitGeneralSearch = () => {
    const query = generalSearch.trim();
    if (!query) return;
    const encoded = encodeURIComponent(query);
    const targetByScope = {
      all: `/search?q=${encoded}`,
      cars: `/buy/results?search=${encoded}`,
      parts: `/parts?search=${encoded}`,
      services: `/services?search=${encoded}`,
      workshops: `/maintenance?search=${encoded}`,
      marketplace: `/marketplace?search=${encoded}`,
    };
    router.push(targetByScope[generalSearchScope]);
  };

  return (
    <div className="flex flex-col w-full">
      <header className="md:hidden p-4 flex items-center justify-between bg-[#EEF2FF] dark:bg-slate-900/95 border-b border-indigo-100 dark:border-slate-800 sticky top-0 z-50 shadow-sm backdrop-blur-md bg-opacity-95 -mx-4">
        <div className="flex items-center gap-2">
          {branding.logo_url ? (
            <img src={branding.logo_url} alt={branding.app_name_ar} className="h-9 w-auto object-contain" />
          ) : (
            <>
              <div className="bg-primary p-2 rounded-xl shadow-lg relative overflow-hidden">
                <Car className="h-6 w-6 text-white" />
              </div>
              <h1 className="text-xl font-black text-slate-800 dark:text-white leading-none">
                {branding.app_name_ar}<span className="text-primary">.com</span>
              </h1>
            </>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={toggleLanguage}
            title={lang === 'ar' ? 'Switch to English' : 'التبديل إلى العربية'}
            className="h-9 px-2.5 rounded-full bg-white dark:bg-slate-800 text-primary border border-indigo-50 dark:border-slate-700 shadow-sm flex items-center gap-1.5"
          >
            <Globe className="h-4 w-4" />
            <span className="text-[11px] font-black">{lang === 'ar' ? 'EN' : 'AR'}</span>
          </button>

          <button
            type="button"
            onClick={toggleTheme}
            title={isDark ? 'تفعيل الوضع النهاري' : 'تفعيل الوضع الليلي'}
            className="h-9 w-9 rounded-full bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-300 border border-indigo-50 dark:border-slate-700 shadow-sm flex items-center justify-center"
          >
            {isDark ? <Sun className="h-4 w-4 text-yellow-400" /> : <Moon className="h-4 w-4" />}
          </button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="h-9 w-9 rounded-full bg-white dark:bg-slate-800 text-primary border border-indigo-50 dark:border-slate-700 shadow-sm flex items-center justify-center">
                <Globe className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48 rounded-2xl p-2 shadow-xl border-none">
              {COUNTRIES.map((c) => (
                <DropdownMenuItem key={c.code} onClick={() => setSelectedCountryCode(c.code)} className="font-bold">
                  {c.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <Link href="/chat">
            <div className="h-9 w-9 rounded-full bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-300 border border-indigo-50 dark:border-slate-700 shadow-sm flex items-center justify-center">
              <MessageSquare className="h-4 w-4" />
            </div>
          </Link>
        </div>
      </header>

      <div className="space-y-8 py-6 max-w-6xl mx-auto w-full">
        <section className="relative isolate -mx-4 overflow-hidden bg-slate-950 px-4 py-8 text-white shadow-xl md:mx-0 md:rounded-lg md:px-10 md:py-12">
          <img
            src={heroImage}
            alt={lang === 'ar' ? 'سيارة رياضية في واجهة عرض حديثة' : 'Modern sports car in a showroom'}
            className="absolute inset-0 h-full w-full object-cover opacity-55"
          />
          <div className="absolute inset-0 bg-[linear-gradient(115deg,rgba(2,6,23,0.96)_0%,rgba(15,23,42,0.82)_46%,rgba(37,99,235,0.45)_100%)]" />
          <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-orange-500/25 to-transparent" />

          <div className="relative grid min-h-[500px] items-center gap-8 md:grid-cols-[1.05fr_0.95fr]">
            <div className="max-w-2xl space-y-6">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-2 text-[11px] font-black uppercase tracking-[0.18em] text-blue-100 backdrop-blur">
                <BadgeCheck className="h-4 w-4 text-orange-300" />
                {lang === 'ar' ? 'سوق سيارات متكامل' : 'Advanced automotive marketplace'}
              </div>

              <div className="space-y-3">
                <h1 className="max-w-3xl text-4xl font-black leading-tight text-white md:text-6xl">
                  {lang === 'ar' ? 'بيع، شراء، قطع غيار وخدمات في تجربة واحدة.' : 'Buy, sell, parts, and services in one polished flow.'}
                </h1>
                <p className="max-w-xl text-sm font-bold leading-7 text-slate-200 md:text-base">
                  {lang === 'ar'
                    ? 'واجهة أسرع للبحث عن السيارة المناسبة، نشر إعلان للبيع، أو الوصول لقطع الغيار والورش بدون ازدحام.'
                    : 'A clearer way to find the right car, publish a listing, or reach spare parts and workshops without clutter.'}
                </p>
              </div>

              <div className="group space-y-3">
                <form
                  className="relative"
                  suppressHydrationWarning
                  onSubmit={(event) => {
                    event.preventDefault();
                    submitGeneralSearch();
                  }}
                >
                  <Search className={`absolute ${isRTL ? 'right-5' : 'left-5'} top-1/2 h-5 w-5 -translate-y-1/2 text-blue-200 transition-colors group-focus-within:text-orange-300`} />
                  <input
                    type="search"
                    placeholder={t('search_placeholder')}
                    value={generalSearch}
                    suppressHydrationWarning
                    onChange={(event) => setGeneralSearch(event.target.value)}
                    className={`h-14 w-full rounded-lg border border-white/15 bg-white/95 text-sm font-bold text-slate-900 shadow-2xl outline-none transition-all placeholder:text-slate-400 focus:ring-4 focus:ring-blue-400/25 ${isRTL ? 'pr-12 pl-28' : 'pl-12 pr-28'}`}
                  />
                  <Button
                    type="submit"
                    className={`absolute top-1/2 h-10 -translate-y-1/2 rounded-md bg-orange-500 px-5 font-black text-white shadow-lg hover:bg-orange-600 disabled:bg-slate-300 ${isRTL ? 'left-2' : 'right-2'}`}
                    disabled={!generalSearch.trim()}
                  >
                    {lang === 'ar' ? 'بحث' : 'Search'}
                  </Button>
                </form>
                <div className="flex gap-2 overflow-x-auto pb-1">
                  {searchScopes.map((scope) => (
                    <button
                      key={scope.id}
                      type="button"
                      onClick={() => setGeneralSearchScope(scope.id)}
                      className={`shrink-0 rounded-full border px-4 py-2 text-[11px] font-black transition-colors ${
                        generalSearchScope === scope.id
                          ? 'border-blue-400 bg-blue-500 text-white'
                          : 'border-white/15 bg-white/10 text-blue-50 hover:border-orange-300 hover:text-orange-200'
                      }`}
                    >
                      {scope.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex flex-wrap gap-3">
                <Button asChild className="h-11 rounded-md bg-blue-600 px-5 font-black text-white hover:bg-blue-700">
                  <Link href="/buy">
                    {lang === 'ar' ? 'ابدأ الشراء' : 'Start Buying'}
                    <ArrowUpRight className="h-4 w-4" />
                  </Link>
                </Button>
                <Button asChild variant="outline" className="h-11 rounded-md border-orange-300 bg-white/10 px-5 font-black text-white backdrop-blur hover:bg-orange-500 hover:text-white">
                  <Link href="/sell">
                    {lang === 'ar' ? 'بيع سيارتي' : 'Sell My Car'}
                    <CircleDollarSign className="h-4 w-4" />
                  </Link>
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-3 md:hidden">
                {mainServices.slice(0, 4).map((service) => (
                  <Link key={service.id} href={service.href} className="flex items-center gap-3 rounded-lg border border-white/15 bg-white/10 p-3 text-white backdrop-blur">
                    <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-md ${service.color}`}>
                      <service.icon className="h-5 w-5" />
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-black">{service.label}</span>
                      <span className="block truncate text-[10px] font-bold uppercase tracking-widest text-blue-100">{service.sub}</span>
                    </span>
                  </Link>
                ))}
              </div>
            </div>

            <div className="relative hidden min-h-[440px] md:block">
              <div className="absolute inset-8 rounded-full border border-white/10 bg-blue-500/10 blur-3xl" />
              {mainServices.slice(0, 8).map((service, index) => (
                <Link
                  key={service.id}
                  href={service.href}
                  className={`absolute ${service.floatClass} group/floating flex w-48 items-center gap-3 rounded-lg border border-white/15 bg-white/[0.12] p-3 text-white shadow-2xl backdrop-blur-xl transition-all hover:-translate-y-1 hover:border-orange-300/70 hover:bg-white/20`}
                  style={{ animationDelay: `${index * 120}ms` }}
                >
                  <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-md ${service.color} ring-1`}>
                    <service.icon className="h-5 w-5" />
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-black">{service.label}</span>
                    <span className="block truncate text-[10px] font-bold uppercase tracking-widest text-blue-100">{service.sub}</span>
                  </span>
                  <ArrowUpRight className="ms-auto h-4 w-4 shrink-0 text-orange-300 opacity-0 transition-opacity group-hover/floating:opacity-100" />
                </Link>
              ))}
              <div className="absolute bottom-20 left-1/2 h-24 w-24 -translate-x-1/2 rounded-full border border-orange-300/30 bg-orange-400/15" />
              <ShieldCheck className="absolute bottom-28 left-1/2 h-10 w-10 -translate-x-1/2 text-orange-200" />
            </div>
          </div>
        </section>

        {/* Optional hero banner from branding */}
        {branding.homepage_banner_url && (
          <section className="px-2">
            <div className="relative rounded-[2rem] overflow-hidden shadow-lg">
              <img
                src={branding.homepage_banner_url}
                alt={branding.homepage_banner_title_ar || branding.app_name_ar}
                className="w-full h-36 md:h-56 object-cover"
              />
              {(branding.homepage_banner_title_ar || branding.homepage_banner_subtitle_ar) && (
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex flex-col justify-end p-5">
                  {branding.homepage_banner_title_ar && (
                    <h2 className="text-white font-black text-xl leading-tight drop-shadow">
                      {branding.homepage_banner_title_ar}
                    </h2>
                  )}
                  {branding.homepage_banner_subtitle_ar && (
                    <p className="text-white/80 text-xs font-bold mt-1">
                      {branding.homepage_banner_subtitle_ar}
                    </p>
                  )}
                </div>
              )}
            </div>
          </section>
        )}

        {/* AI Recommendations Hub (Behavior based) */}
        <section className="px-2">
          <AIPersonalizedRecommendations />
        </section>

        {/* Homepage Ads Demo */}
        {(adsLoading || homeAds.length > 0) && (
          <section className="space-y-4 px-2">
            <div className="flex items-center gap-2 px-2">
              <Film className="h-4 w-4 text-primary" />
              <h2 className="text-xs font-black text-slate-400 uppercase tracking-widest">
                {lang === 'ar' ? 'إعلانات تجريبية' : 'Demo Ads'}
              </h2>
            </div>

            {adsLoading ? (
              <div className="flex gap-4 overflow-hidden px-2">
                {[1, 2, 3].map((idx) => (
                  <div key={idx} className="min-w-[88px] animate-pulse">
                    <div className="h-20 w-20 rounded-full bg-slate-100 mx-auto" />
                    <div className="h-3 w-16 bg-slate-100 rounded mx-auto mt-2" />
                    <div className="h-2.5 w-14 bg-slate-100 rounded mx-auto mt-1" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex gap-4 overflow-x-auto pb-2 px-1 snap-x">
                {homeAds.map((ad) => {
                  const title = lang === 'ar' ? (ad.title_ar || ad.title) : (ad.title || ad.title_ar);
                  const fallbackEmbed = toYoutubeEmbedUrl(ad.video_url || ad.link_url || '');
                  const embedUrl = ad.youtube_embed_url || fallbackEmbed;
                  const imageUrl = toAbsoluteMediaUrl(ad.image);
                  const youtubeId = toYoutubeVideoId(embedUrl || ad.video_url || ad.link_url || '');
                  const thumbnailUrl = imageUrl || (youtubeId ? `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg` : '');

                  return (
                    <button
                      key={ad.id}
                      type="button"
                      onClick={() => setSelectedStoryAd(ad)}
                      className="min-w-[92px] flex flex-col items-center snap-start"
                    >
                      <div className="rounded-full p-[2px] bg-gradient-to-tr from-primary via-indigo-400 to-sky-300 shadow-md">
                        <div className="rounded-full p-[2px] bg-white">
                          <div className="h-20 w-20 rounded-full overflow-hidden bg-slate-100 border border-slate-100">
                            {thumbnailUrl ? (
                              <img src={thumbnailUrl} alt={title} className="h-full w-full object-cover" />
                            ) : (
                              <div className="h-full w-full flex items-center justify-center text-slate-400">
                                <Film className="h-5 w-5" />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <p className="text-[11px] font-bold text-slate-700 mt-2 text-center line-clamp-2 leading-tight">
                        {title}
                      </p>
                      <p className="text-[10px] text-slate-400 font-bold">
                        {lang === 'ar' ? 'شريك معلن' : 'Ad Partner'}
                      </p>
                    </button>
                  );
                })}
              </div>
            )}

            <Dialog open={Boolean(selectedStoryAd)} onOpenChange={(open) => !open && setSelectedStoryAd(null)}>
              <DialogContent className="rounded-[2rem] p-4 sm:p-5 max-w-md border-none shadow-2xl">
                {selectedStoryAd && (() => {
                  const title = lang === 'ar'
                    ? (selectedStoryAd.title_ar || selectedStoryAd.title)
                    : (selectedStoryAd.title || selectedStoryAd.title_ar);
                  const embedUrl = selectedStoryAd.youtube_embed_url
                    || toYoutubeEmbedUrl(selectedStoryAd.video_url || selectedStoryAd.link_url || '');
                  const imageUrl = toAbsoluteMediaUrl(selectedStoryAd.image);
                  return (
                    <div className="space-y-3">
                      <DialogHeader>
                        <DialogTitle className="text-base font-black text-slate-800 line-clamp-2">
                          {title}
                        </DialogTitle>
                      </DialogHeader>

                      <div className="rounded-[1.25rem] overflow-hidden bg-slate-100 border border-slate-100 aspect-[9/16] max-h-[70vh]">
                        {embedUrl ? (
                          <iframe
                            src={embedUrl}
                            title={title}
                            className="w-full h-full"
                            loading="lazy"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            allowFullScreen
                          />
                        ) : imageUrl ? (
                          <img src={imageUrl} alt={title} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-slate-400 text-sm font-bold">
                            {lang === 'ar' ? 'إعلان بدون وسائط' : 'Ad without media'}
                          </div>
                        )}
                      </div>

                      {selectedStoryAd.link_url && (
                        <a
                          href={selectedStoryAd.link_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-sm font-bold text-primary hover:text-indigo-700"
                        >
                          {lang === 'ar' ? 'فتح رابط الشريك' : 'Open Partner Link'}
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      )}
                    </div>
                  );
                })()}
              </DialogContent>
            </Dialog>
          </section>
        )}

        <div className="md:hidden flex items-center gap-2 px-4 py-2 bg-indigo-50/50 rounded-2xl border border-indigo-50 mx-2 overflow-hidden">
          <Sparkles className="h-4 w-4 text-primary shrink-0 animate-pulse" />
          <p className="text-[10px] font-bold text-slate-600 truncate">
            {activeTips[currentTipIndex].text_ar}
          </p>
        </div>

        <section className="space-y-4">
          <div className="flex items-end justify-between gap-3 px-2">
            <div>
              <p className="text-[11px] font-black uppercase tracking-[0.18em] text-orange-500">{lang === 'ar' ? 'اختر المسار' : 'Choose your path'}</p>
              <h2 className="mt-1 text-2xl font-black text-slate-900 dark:text-white">{t('main_services')}</h2>
            </div>
            <div className="hidden items-center gap-2 text-xs font-bold text-slate-500 md:flex">
              <span className="h-2 w-2 rounded-full bg-blue-600" />
              {lang === 'ar' ? 'الأزرق للشراء والبحث' : 'Blue for search and buying'}
              <span className="ms-2 h-2 w-2 rounded-full bg-orange-500" />
              {lang === 'ar' ? 'البرتقالي للبيع والخدمات' : 'Orange for selling and service'}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 px-2 md:grid-cols-3 lg:grid-cols-4">
            {mainServices.map((service) => (
              <Link key={service.id} href={service.href} prefetch={true}>
                <Card className="group relative h-[154px] overflow-hidden rounded-lg border border-slate-200/80 bg-white p-4 shadow-sm transition-all hover:-translate-y-1 hover:border-blue-200 hover:shadow-xl dark:border-slate-800 dark:bg-slate-900">
                  <div className={`absolute -bottom-10 -end-8 h-28 w-28 rounded-full bg-gradient-to-br ${service.accent} opacity-10 transition-opacity group-hover:opacity-20`} />
                  <service.icon className="absolute bottom-3 end-3 h-16 w-16 text-slate-900/[0.04] transition-transform group-hover:scale-110 dark:text-white/[0.05]" />
                  <div className="relative flex h-full flex-col justify-between">
                    <div className="flex items-start gap-3">
                      <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-lg ${service.color} ring-1 transition-all group-hover:scale-105`}>
                        <service.icon className="h-6 w-6" />
                      </div>
                      <div className="min-w-0 pt-0.5">
                        <span className="block truncate text-base font-black leading-6 text-slate-900 dark:text-white">{service.label}</span>
                        <span className="block truncate text-[10px] font-black uppercase tracking-widest text-slate-400">{service.sub}</span>
                      </div>
                    </div>
                    <p className="line-clamp-2 text-xs font-bold leading-5 text-slate-500 dark:text-slate-400">{service.desc}</p>
                    <div className="flex items-center justify-between text-[11px] font-black text-blue-600">
                      <span>{lang === 'ar' ? 'فتح' : 'Open'}</span>
                      <ArrowUpRight className="h-4 w-4 text-orange-500 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                    </div>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        </section>

        <section className="space-y-4">
          <div className="flex items-center gap-2 px-4">
            <Sparkles className="h-5 w-5 text-indigo-500" />
            <h2 className="text-xs font-black text-slate-400 uppercase tracking-widest">{t('ai_services')}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 px-2">
            {aiServices.map((service) => (
              <Link key={service.id} href={service.href} prefetch={true}>
                <Card className="group relative flex h-full items-center gap-4 overflow-hidden rounded-lg border border-slate-200 bg-white p-5 shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl dark:border-slate-800 dark:bg-slate-900">
                  <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-lg ${service.color} shadow-sm transition-transform group-hover:scale-105`}>
                    <service.icon className="h-6 w-6" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <span className="block truncate text-lg font-black text-slate-900 dark:text-white">{service.label}</span>
                    <span className="text-xs text-slate-400 font-bold uppercase tracking-widest">{service.sub}</span>
                  </div>
                  <service.icon className="h-16 w-16 text-slate-900/[0.04] transition-opacity group-hover:opacity-20 dark:text-white/[0.05]" />
                </Card>
              </Link>
            ))}
          </div>
        </section>

        <section className="px-2">
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="outline" className="w-full h-16 rounded-[2rem] border-dashed border-indigo-200 bg-white hover:bg-indigo-50 flex items-center justify-center gap-3 group">
                <div className="p-2 bg-indigo-50 rounded-full group-hover:bg-indigo-100 transition-colors">
                  <Lightbulb className="h-6 w-6 text-primary" />
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold text-slate-800">اعطني نصائح مخصصة</p>
                  <p className="text-[10px] text-slate-400 font-bold uppercase">Smart Advice System</p>
                </div>
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-[2.5rem] p-8 max-w-md border-none shadow-2xl">
              <DialogHeader className="mb-6">
                <DialogTitle className="text-2xl font-black flex items-center gap-3 text-slate-800">
                  <div className="p-3 bg-indigo-50 rounded-2xl">
                    <Sparkles className="h-6 w-6 text-primary" />
                  </div>
                  نصائح الخبير الذكية
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                {getContextualTips('general', 4).map((tip, idx) => (
                  <div key={tip.id} className="flex gap-4 p-4 bg-slate-50 rounded-2xl border border-slate-100 animate-in fade-in slide-in-from-bottom-2" style={{ animationDelay: `${idx * 100}ms` }}>
                    <div className="h-8 w-8 rounded-full bg-white shadow-sm flex items-center justify-center shrink-0">
                      <Info className="h-4 w-4 text-primary" />
                    </div>
                    <p className="text-sm font-bold text-slate-700 leading-relaxed">{tip.text_ar}</p>
                  </div>
                ))}
              </div>
            </DialogContent>
          </Dialog>
        </section>
      </div>
    </div>
  );
}
